import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Loader2, ArrowLeft, CheckCircle2, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import API_URL from '../lib/apiUrl';

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (location.state?.email) setEmail(location.state.email);
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) return setError('Please enter your email');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
      });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Could not send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse at top, rgba(52,211,153,0.08), transparent 60%), linear-gradient(180deg, #060914 0%, #0a0f1f 100%)',
      }}
    >
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative w-full max-w-md"
      >
        <div
          className="rounded-2xl p-8 md:p-10"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(16px)',
            boxShadow: '0 25px 60px -10px rgba(0,0,0,0.5)',
          }}
        >
          <div className="text-center mb-7">
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 border"
              style={{ background: 'rgba(52,211,153,0.08)', borderColor: 'rgba(52,211,153,0.3)' }}
            >
              {submitted ? (
                <CheckCircle2 className="h-6 w-6 text-emerald-300" />
              ) : (
                <ShieldCheck className="h-6 w-6 text-emerald-300" />
              )}
            </div>
            <h1 className="font-heading font-bold text-2xl text-white mb-2">
              {submitted ? 'Check your inbox' : 'Forgot your password?'}
            </h1>
            <p className="text-slate-400 text-sm">
              {submitted
                ? "We've sent a password reset link if an account with that email exists. The link is valid for 1 hour."
                : "Enter the email associated with your account and we'll send you a reset link."}
            </p>
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div
                  className="px-4 py-3 rounded-lg text-sm text-red-300"
                  style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}
                  data-testid="forgot-error"
                >
                  {error}
                </div>
              )}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    className="w-full pl-10 pr-3 py-3 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 transition-colors"
                    placeholder="you@company.com"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white hover:bg-slate-100 text-slate-900 py-6 font-semibold rounded-lg"
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Reset Link'}
              </Button>
            </form>
          ) : (
            <Button
              onClick={() => navigate('/auth')}
              className="w-full bg-white hover:bg-slate-100 text-slate-900 py-6 font-semibold rounded-lg"
              data-testid="forgot-back-to-signin-btn"
            >
              Back to Sign In
            </Button>
          )}

          <button
            type="button"
            onClick={() => navigate('/auth')}
            className="w-full flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors font-mono uppercase tracking-widest pt-5"
            data-testid="forgot-back-link"
          >
            <ArrowLeft className="h-3 w-3" /> Back to Sign In
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
